import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/landing/components/Header';
import Footer from '@/pages/landing/components/Footer';
import { getUser, isLoggedIn } from '@/auth/token';
import PlaybookCard from './components/PlaybookCard';
import {
  filterMarketplace,
  marketplaceCategories,
  SORT_OPTIONS,
  sortMarketplace,
  toMarketplaceCard,
} from './mapPlaybook';
import type { PublicOutcomeSummary } from '@/pages/landing/outcomeShowcase';

export default function Marketplace() {
  const [outcomes, setOutcomes] = useState<PublicOutcomeSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('popular');
  const logged = isLoggedIn();
  const isAdmin = getUser()?.role === 'admin';

  useEffect(() => {
    document.title = 'Playbook Marketplace · Sourcetape';
    fetch('/api/outcomes/public')
      .then((res) => {
        if (!res.ok) throw new Error('failed');
        return res.json();
      })
      .then((data) => {
        setOutcomes(Array.isArray(data) ? data : []);
        setLoadError(null);
      })
      .catch(() => {
        setOutcomes([]);
        setLoadError('Could not reach the outcome registry.');
      })
      .finally(() => setLoading(false));
  }, []);

  const cards = useMemo(() => outcomes.map(toMarketplaceCard), [outcomes]);
  const categories = useMemo(() => marketplaceCategories(cards), [cards]);
  const filtered = useMemo(
    () => sortMarketplace(filterMarketplace(cards, { search, category }), sort),
    [cards, search, category, sort],
  );

  const totalDownloads = cards.reduce((sum, p) => sum + p.downloads, 0);
  const creators = new Set(cards.map((p) => p.author)).size;

  return (
    <div className="min-h-screen bg-bg text-textp" data-testid="marketplace-page">
      <Header logged={logged} isAdmin={isAdmin} />

      <main>
        <section className="relative overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(74,213,202,0.16), transparent)' }}
          />
          <div
            className="absolute inset-0 dot-grid opacity-60"
            style={{
              maskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%)',
              WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 0%, black 40%, transparent 100%)',
            }}
          />

          <div className="relative max-w-[1040px] mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-20">
            <div className="max-w-[640px]">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-text bg-accent px-3 py-1 rounded-full">
                <i className="ri-store-2-line" />
                Marketplace
              </span>
              <h1 className="mt-4 font-heading text-3xl md:text-5xl tracking-tight font-semibold text-textp">
                Tested playbooks, ready to run
              </h1>
              <p className="mt-4 text-texts leading-relaxed max-w-[540px]">
                Browse playbooks composed and scored by the community. Every one ships with its SkillsBench
                score, step breakdown, and portable SKILL.md export.
              </p>

              <div className="mt-8 flex items-center gap-2 bg-surface border border-border rounded-full pl-4 pr-1.5 py-1.5 max-w-[440px]">
                <i className="ri-search-line text-texts" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search playbooks, authors, or tags"
                  className="flex-1 bg-transparent text-sm text-textp placeholder:text-texts outline-none"
                />
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="whitespace-nowrap text-xs font-medium text-accent hover:text-accent-hover bg-accentSoft px-3 py-1.5 rounded-full transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
              <div>
                <div className="text-2xl font-mono text-textp">{cards.length}</div>
                <div className="text-xs text-texts">playbooks</div>
              </div>
              <div>
                <div className="text-2xl font-mono text-textp">{totalDownloads.toLocaleString()}</div>
                <div className="text-xs text-texts">downloads</div>
              </div>
              <div>
                <div className="text-2xl font-mono text-textp">{creators}</div>
                <div className="text-xs text-texts">creators</div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-t border-border">
          <div className="max-w-[1040px] mx-auto px-6 py-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-center gap-1.5 flex-wrap">
                {categories.map((c) => {
                  const active = category === c.value;
                  return (
                    <button
                      key={c.value}
                      type="button"
                      onClick={() => setCategory(c.value)}
                      className={`whitespace-nowrap text-sm px-3 py-1.5 rounded-full transition-colors ${
                        active
                          ? 'bg-accent text-accent-text'
                          : 'bg-surface2 border border-border text-texts hover:text-textp'
                      }`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>

              <div className="lg:ml-auto flex items-center gap-2">
                <span className="text-sm text-texts whitespace-nowrap">{filtered.length} results</span>
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="appearance-none bg-surface border border-border rounded-lg pl-3 pr-9 py-1.5 text-sm text-textp outline-none"
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line text-texts text-sm absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-16">
          <div className="max-w-[1040px] mx-auto px-6">
            {loading ? (
              <p className="text-sm text-texts py-12">Loading playbooks…</p>
            ) : loadError ? (
              <p className="text-sm text-texts py-12">{loadError}</p>
            ) : filtered.length === 0 ? (
              <div className="bg-surface border border-border rounded-xl p-14 text-center">
                <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-xl bg-accentSoft border border-accentLine">
                  <i className="ri-search-line text-accent text-2xl" />
                </span>
                <h2 className="mt-4 text-base font-semibold text-textp">No playbooks found</h2>
                <p className="mt-1 text-sm text-texts">Try a different search or category.</p>
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setCategory('all');
                  }}
                  className="whitespace-nowrap mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover px-4 py-2 rounded-lg transition-colors"
                >
                  Reset filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((p) => (
                  <PlaybookCard key={p.id} playbook={p} />
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="border-t border-border py-16 md:py-20">
          <div className="max-w-[1040px] mx-auto px-6">
            <div className="bg-surface border border-border rounded-2xl p-8 md:p-12 text-center">
              <span className="w-12 h-12 mx-auto flex items-center justify-center rounded-xl bg-accentSoft border border-accentLine">
                <i className="ri-price-tag-3-line text-accent text-xl" />
              </span>
              <h2 className="mt-5 font-heading text-2xl md:text-3xl tracking-tight font-semibold text-textp">
                Turn your playbooks into revenue
              </h2>
              <p className="mt-3 text-texts leading-relaxed max-w-[480px] mx-auto">
                Publish the playbooks you build in Studio, set a price, and earn when the community downloads them.
              </p>
              <Link
                to="/dashboard?view=playbooks"
                className="whitespace-nowrap mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-accent-text bg-accent hover:bg-accent-hover px-5 py-2.5 rounded-lg transition-colors"
              >
                Set your prices
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer logged={logged} />
    </div>
  );
}
